// Shared drag + QWERTY→Ottoman mapping for search bar and grid keyboards.
import { isOttomanText } from "../../lib/ottoman_text_utils"

// localStorage key for persisting Ottoman keyboard drag position across sessions
export const LEXIQAMUS_OTTOMAN_KEYBOARD_DRAG_KEY = "lexiqamusSearchOttomanKeyboardPosition"
// Separate drag key for TypeO word edit — search bar clears its key on every page load.
export const TYPEO_OTTOMAN_KEYBOARD_DRAG_KEY = "typeoOttomanKeyboardPosition"

// CSS class applied during active drag operation
export const LEXIQAMUS_OTTOMAN_KEYBOARD_DRAGGED_CLASS = "lexiqamus-search-keyboard--dragging"

/*
 * Safe boundary margin for screen accessibility:
 * Minimum gap (in pixels) kept between the floating keyboard and the edge
 * of the screen so the keyboard never gets pushed outside the visible area.
 */
const FLOATING_KEYBOARD_DRAG_VIEWPORT_MARGIN_PX = 4

/*
 * Boundary guard helper for accessibility:
 * Keeps a position value between an allowed minimum and maximum.
 * Prevents the keyboard from wandering off the screen while dragging.
 */
function clampLexiqamusDrag(n, min, max) {
  return Math.min(max, Math.max(min, n))
}

/*
 * Accessibility helper for floating keyboard layout:
 * Positions the keyboard panel at specific screen coordinates (left, top).
 * Resets conflicting right/bottom positioning so the panel moves smoothly.
 */
function setFloatingKeyboardPanelPosition(panelEl, left, top) {
  panelEl.style.left = `${left}px`
  panelEl.style.top = `${top}px`
  panelEl.style.right = "auto"
  panelEl.style.bottom = "auto"
  panelEl.style.transform = "none"
}

function clampFloatingKeyboardPanelPosition(panelEl, left, top, margin = FLOATING_KEYBOARD_DRAG_VIEWPORT_MARGIN_PX) {
  const { width, height } = panelEl.getBoundingClientRect()
  setFloatingKeyboardPanelPosition(
    panelEl,
    clampLexiqamusDrag(left, margin, window.innerWidth - width - margin),
    clampLexiqamusDrag(top, margin, window.innerHeight - height - margin)
  )
}

// Attaches drag functionality to a floating keyboard panel via a drag handle.
// Persists position to localStorage when storageKey is set (defaults to
// LEXIQAMUS_OTTOMAN_KEYBOARD_DRAG_KEY for the search-bar keyboard).
// Pass storageKey null to skip localStorage (Word Decoder keeps position in memory).
// options.onDragEnd runs after a completed drag; options.onRestoreDefaultPosition runs on Home.
// Returns cleanup function to remove event listeners.
export function attachLexiqamusOttomanKeyboardDrag(panelEl, dragHandleEl, storageKey = LEXIQAMUS_OTTOMAN_KEYBOARD_DRAG_KEY, options = {}) {
  if (typeof options === "function") options = { onDragEnd: options }
  if (!panelEl || !dragHandleEl) return () => {}

  let armed = false
  let dragging = false
  let offsetX = 0
  let offsetY = 0

  const capturePanelPositionFromViewport = () => {
    const { left, top } = panelEl.getBoundingClientRect()
    setFloatingKeyboardPanelPosition(panelEl, left, top)
  }

  // Saves current panel position to localStorage for persistence across sessions.
  const persist = () => {
    if (!storageKey) return // Word Decoder: in-memory only via onDragEnd + close/open
    try {
      window.localStorage.setItem(
        storageKey,
        JSON.stringify({ top: panelEl.style.top, left: panelEl.style.left })
      )
    } catch (_e) {
      // ignore — localStorage may be unavailable (e.g. private browsing)
    }
  }

  // Updates panel position during drag, clamped to viewport bounds with margin
  const onPointerMove = (e) => {
    if (!armed || !dragging) return
    e.preventDefault()
    const panelWidth  = panelEl.offsetWidth
    const panelHeight = panelEl.offsetHeight
    const margin = FLOATING_KEYBOARD_DRAG_VIEWPORT_MARGIN_PX
    const left = clampLexiqamusDrag(e.clientX - offsetX, margin, window.innerWidth - panelWidth - margin)
    const top  = clampLexiqamusDrag(e.clientY - offsetY, margin, window.innerHeight - panelHeight - margin)
    setFloatingKeyboardPanelPosition(panelEl, left, top)
  }

  // Cleans up drag state and persists final position
  const endDrag = () => {
    if (!armed) return
    armed = false
    document.removeEventListener("pointermove", onPointerMove)
    document.removeEventListener("pointerup", onPointerUp)
    document.removeEventListener("pointercancel", onPointerUp)
    if (dragging) {
      panelEl.classList.remove(LEXIQAMUS_OTTOMAN_KEYBOARD_DRAGGED_CLASS)
      persist()
      if (typeof options.onDragEnd === "function") options.onDragEnd() // e.g. Word Decoder: stop auto-follow under letter grid
    }
    dragging = false
  }

  const onPointerUp = () => {
    endDrag()
  }

  // Initiates drag on left-click of drag handle (excludes action buttons)
  const onPointerDown = (e) => {
    if (e.button !== 0) return
    if (e.target.closest(".ottoman-keyboard-action-btn")) return
    if (e.target.closest("button")) return
    if (!dragHandleEl.contains(e.target)) return

    e.preventDefault()
    armed   = true
    dragging = true
    capturePanelPositionFromViewport()
    offsetX = e.clientX - panelEl.getBoundingClientRect().left
    offsetY = e.clientY - panelEl.getBoundingClientRect().top
    panelEl.classList.add(LEXIQAMUS_OTTOMAN_KEYBOARD_DRAGGED_CLASS)
    document.addEventListener("pointermove", onPointerMove, { passive: false })
    document.addEventListener("pointerup", onPointerUp, true)
    document.addEventListener("pointercancel", onPointerUp, true)
  }

  /*
   * Accessibility keyboard shortcuts for repositioning the floating keyboard:
   * When using the keyboard rather than a mouse, this allows moving the keyboard:
   * - Arrow keys: Move the keyboard in any direction (Shift + Arrow for larger jumps).
   * - Home key: Resets the keyboard back to its default anchored position.
   * - Alt + 1/2/3/4: Docks the keyboard into screen corners (1: top-left, 2: top-right,
   *   3: bottom-left, 4: bottom-right).
   * Key events on nested buttons (such as Backspace or Close) are ignored here
   * so they continue to trigger their own button actions without moving the panel.
   */
  const onDragHandleKeydown = (e) => {
    if (e.target.closest("button")) return

    const { left, top } = panelEl.getBoundingClientRect()
    /*
     * Accessibility movement step size:
     * Holding Shift moves the keyboard by 48 pixels for faster traversal.
     * Regular arrow keys move it by 12 pixels for fine adjustments.
     */
    const step = e.shiftKey ? 48 : 12

    if (e.key === "ArrowLeft") {
      e.preventDefault()
      clampFloatingKeyboardPanelPosition(panelEl, left - step, top)
      persist()
    } else if (e.key === "ArrowRight") {
      e.preventDefault()
      clampFloatingKeyboardPanelPosition(panelEl, left + step, top)
      persist()
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      clampFloatingKeyboardPanelPosition(panelEl, left, top - step)
      persist()
    } else if (e.key === "ArrowDown") {
      e.preventDefault()
      clampFloatingKeyboardPanelPosition(panelEl, left, top + step)
      persist()
    } else if (e.key === "Home") {
      e.preventDefault()
      options.onRestoreDefaultPosition?.()
    } else if (e.altKey && ["1", "2", "3", "4"].includes(e.key)) {
      e.preventDefault()
      const dockMargin = 8
      const panelWidth = panelEl.offsetWidth
      const panelHeight = panelEl.offsetHeight
      const cornerPositions = {
        1: { left: dockMargin, top: dockMargin },
        2: { left: window.innerWidth - panelWidth - dockMargin, top: dockMargin },
        3: { left: dockMargin, top: window.innerHeight - panelHeight - dockMargin },
        4: { left: window.innerWidth - panelWidth - dockMargin, top: window.innerHeight - panelHeight - dockMargin }
      }
      const cornerPosition = cornerPositions[parseInt(e.key, 10)]
      if (cornerPosition) setFloatingKeyboardPanelPosition(panelEl, cornerPosition.left, cornerPosition.top)
      persist()
    }
  }

  dragHandleEl.addEventListener("pointerdown", onPointerDown)
  dragHandleEl.addEventListener("keydown", onDragHandleKeydown)

  return () => {
    dragHandleEl.removeEventListener("pointerdown", onPointerDown)
    dragHandleEl.removeEventListener("keydown", onDragHandleKeydown)
    endDrag()
  }
}

// Maps Turkish QWERTY key characters to Ottoman characters for Turkish locale.
// Uses event.key (actual typed character) rather than event.code (physical position)
// because Turkish keys (ı, ğ, ü, ş, ö, ç) differ from standard QWERTY positions.
// Supports Shift modifiers and ALT combinations.
export function ottomanCharForTurkishKey(key, shift, alt) {
  // Turkish case: "I" lowers to "ı", "İ" lowers to "i". Plain toLowerCase folds both into "i".
  const k = key.toLocaleLowerCase("tr")

  if (alt && !shift) {
    const altMap = { z: "ذ", s: "ث", t: "ة" }
    return altMap[k] || null
  }

  if (alt) return null

  // A listed Shift letter replaces the base one. Every other key keeps its base letter,
  // so ı/I, ç/Ç, ğ/Ğ and ş/Ş do not depend on Shift being up.
  if (shift) {
    const shiftMap = { t: "ط", a: "ء", s: "ص", d: "ض", h: "ح", z: "ظ", n: "ڭ", w: "ؤ", e: "ۀ", y: "ئ" }
    if (shiftMap[k]) return shiftMap[k]
  }

  const base = {
    q: "ق", w: "و", e: "ە", r: "ر", t: "ت",
    y: "ی", u: "و", ı: "ع", o: "و", p: "پ",
    ğ: "غ", ü: "و",
    a: "ا", s: "س", d: "د", f: "ف", g: "گ",
    h: "ه", j: "ژ", k: "ك", l: "ل", ş: "ش",
    i: "ع",
    z: "ز", x: "خ", c: "ج", v: "و", b: "ب",
    n: "ن", m: "م", ö: "و", ç: "چ"
  }
  return base[k] || null
}

// Maps physical keyboard keys to Ottoman characters for LexiQamus Ottoman mode.
// Supports base keys, Shift modifiers, and Alt combinations (ALT+n).
// Returns Ottoman character or null if no mapping exists.
//
// Note: multiple Latin keys intentionally map to the same Ottoman character
// (e.g. U/O/V → و, Y/I → ی) — this mirrors the Ottoman phonetic layout.
export function ottomanCharForPhysicalKey(code, shift, alt) {
  // Multi-character keys with Shift and Alt variants
  switch (code) {
    case "KeyW":
      if (alt) return null
      return shift ? "ؤ" : "و"
    case "KeyE":
      if (alt) return null
      return shift ? "ۀ" : "ع"
    case "KeyY":
      if (alt) return null
      return shift ? "ئ" : "ی"
    case "KeyT":
      if (alt && !shift) return "ث"
      if (shift && !alt) return "ط"
      if (!shift && !alt) return "ت"
      return null
    case "KeyA":
      if (alt) return null
      return shift ? "ء" : "ا"
    case "KeyS":
      if (alt && !shift) return "ص"
      if (shift && !alt) return "ش"
      if (!shift && !alt) return "س"
      return null
    case "KeyD":
      if (alt) return null
      return shift ? "ض" : "د"
    case "KeyG":
      if (alt) return null
      return shift ? "غ" : "ج"
    case "KeyH":
      if (alt && !shift) return "ة"
      if (shift && !alt) return "ح"
      if (!shift && !alt) return "ه"
      return null
    case "KeyK":
      if (alt) return null
      return shift ? "گ" : "ك"
    case "KeyZ":
      if (alt && !shift) return "ذ"
      if (shift && !alt) return "ظ"
      if (!shift && !alt) return "ز"
      return null
    case "KeyN":
      if (alt) return null
      return shift ? "ڭ" : "ن"
    default:
      break
  }

  // Single-character keys (no Shift/Alt variants)
  if (alt || shift) return null

  const singles = {
    KeyQ: "ق",
    KeyR: "ر",
    KeyU: "و",
    KeyI: "ی",
    KeyO: "و",
    KeyP: "پ",
    KeyF: "ف",
    KeyJ: "ژ",
    KeyL: "ل",
    KeyX: "خ",
    KeyC: "چ",
    KeyV: "و",
    KeyB: "ب",
    KeyM: "م"
  }
  return singles[code] || null
}

const OTTOMAN_KEYDOWN_PASSTHROUGH_KEYS = [
  // Navigation, editing, and submit keys — never mapped to Ottoman characters.
  "Backspace",
  "Delete",
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
  "Home",
  "End",
  "Tab",
  "Enter",
  "Escape"
]

// Shared by Lexiqamus search bar and TypeO typing — returns null when the browser should handle the key.
export function resolveOttomanCharFromKeydown(event, isTurkish) {
  if (event.ctrlKey || event.metaKey) return null
  if (event.isComposing) return null
  if (OTTOMAN_KEYDOWN_PASSTHROUGH_KEYS.includes(event.key)) return null

  // Script-producing physical keyboards (Osmanlıca, Arabic, Urdu) already supply the
  // correct Ottoman/Arabic character in event.key — do not replace with LexiQamus
  // on-screen layout (e.g. Osmanlıca KeyC → ج must not become LexiQamus KeyC → چ).
  // Refrence ticket: https://evolving-bits.atlassian.net/jira/software/projects/CPG/boards/1?selectedIssue=CPG-179
  if (event.key.length === 1 && isOttomanText(event.key)) return null

  if (isTurkish) {
    return ottomanCharForTurkishKey(event.key, event.shiftKey, event.altKey)
  }
  return ottomanCharForPhysicalKey(event.code, event.shiftKey, event.altKey)
}

/*
 * Accessibility key discovery:
 * Collects all active, visible keys inside the keyboard panel.
 * Skips keys that should not receive focus:
 * 1. Disabled keys or keys marked hidden for screen reader accessibility (aria-hidden).
 * 2. Keys on hidden sub-panels (for example, the letter grid switches between
 *    basic and advanced panels, so keys on the inactive panel are ignored).
 * 3. Keys hidden by CSS (display: none or visibility: hidden).
 */
function queryVisibleKeyboardControls(keyboardRoot, selector) {
  if (!keyboardRoot) return []

  return [...keyboardRoot.querySelectorAll(selector)].filter((keyboardElement) => {
    if (keyboardElement.disabled || keyboardElement.getAttribute("aria-hidden") === "true") return false
    if (keyboardElement.closest(".lq-gk-panel--hidden-left, .lq-gk-panel--hidden-right")) return false
    const elementStyle = window.getComputedStyle(keyboardElement)
    return elementStyle.display !== "none" && elementStyle.visibility !== "hidden"
  })
}

/*
 * Accessibility focus resolver:
 * Finds which key currently has keyboard focus.
 * Even if focus is on an inner element inside the key (like an icon or letter label),
 * this finds the main clickable key container.
 */
function resolveFocusedKeyboardControl(focusedElement, rootElement, controlSelector) {
  if (!focusedElement || !rootElement.contains(focusedElement)) return null
  if (focusedElement.matches(controlSelector)) return focusedElement
  const closestMatchingControl = focusedElement.closest(controlSelector)
  return closestMatchingControl && rootElement.contains(closestMatchingControl) ? closestMatchingControl : null
}

/*
 * Accessibility spatial navigation:
 * Moves focus to the closest key in the direction of the pressed arrow key.
 * Compares the visual center coordinates of all keys on the screen to find
 * the nearest neighbor key (up, down, left, or right).
 */
function moveKeyboardFocusInDirection(focusedEl, candidates, direction) {
  if (!focusedEl || candidates.length === 0) return false

  const currentElementRect = focusedEl.getBoundingClientRect()
  const currentCenterX = currentElementRect.left + currentElementRect.width / 2
  const currentCenterY = currentElementRect.top + currentElementRect.height / 2
  let closestElement = null
  let closestElementScore = Infinity

  for (const candidateElement of candidates) {
    if (candidateElement === focusedEl) continue

    const candidateRect = candidateElement.getBoundingClientRect()
    const candidateCenterX = candidateRect.left + candidateRect.width / 2
    const candidateCenterY = candidateRect.top + candidateRect.height / 2
    const deltaX = candidateCenterX - currentCenterX
    const deltaY = candidateCenterY - currentCenterY

    if (direction === "ArrowLeft" && deltaX >= -4) continue
    if (direction === "ArrowRight" && deltaX <= 4) continue
    if (direction === "ArrowUp" && deltaY >= -4) continue
    if (direction === "ArrowDown" && deltaY <= 4) continue

    const primaryAxisDistance = direction === "ArrowLeft" || direction === "ArrowRight" ? Math.abs(deltaX) : Math.abs(deltaY)
    const secondaryAxisDistance = direction === "ArrowLeft" || direction === "ArrowRight" ? Math.abs(deltaY) : Math.abs(deltaX)
    const navigationScore = primaryAxisDistance + secondaryAxisDistance * 2
    if (navigationScore < closestElementScore) {
      closestElementScore = navigationScore
      closestElement = candidateElement
    }
  }

  if (!closestElement) return false
  closestElement.focus()
  return true
}

/*
 * Accessibility arrow key navigation across on-screen keyboard keys:
 * When focus is on any on-screen key, pressing ArrowLeft, ArrowRight,
 * ArrowUp, or ArrowDown moves focus smoothly to the adjacent key.
 * Only intercepts arrow keys when navigating on-screen keys, ensuring
 * normal typing and text cursor movement in search inputs are unaffected.
 */
export function handleKeyboardMovementWithArrowKeys(event, keyboardRoot, keyControlSelector) {
  if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) return false

  const currentlyFocusedControl = resolveFocusedKeyboardControl(document.activeElement, keyboardRoot, keyControlSelector)
  if (!currentlyFocusedControl) return false

  const allVisibleKeyControls = queryVisibleKeyboardControls(keyboardRoot, keyControlSelector)
  if (!allVisibleKeyControls.includes(currentlyFocusedControl)) return false

  event.preventDefault()
  return moveKeyboardFocusInDirection(currentlyFocusedControl, allVisibleKeyControls, event.key)
}

// Shared locale-aware mapper for search bar and Word Decoder physical keyboard input.
export function mapLexiqamusKeyboardEventToOttomanChar({ key, code, shiftKey, altKey, isTurkishLocale }) {
  return isTurkishLocale
    ? ottomanCharForTurkishKey(key, shiftKey, altKey)
    : ottomanCharForPhysicalKey(code, shiftKey, altKey)
}
